import { createElement, lazy, Suspense, forwardRef } from "react";
import Loading from "../modules/app/callback";

const RELOAD_KEY = "markham.lazy-reload-at";
const RELOAD_COOLDOWN = 10000;

function getLastReload() {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch (e) {
    return null;
  }
}

function setLastReload(value) {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(value));
    return true;
  } catch (e) {
    return false;
  }
}

// A tab opened before a deploy will ask for chunk files that no longer exist.
// Reload once to pick up the new build instead of crashing the page.
// The cooldown stops a genuinely broken chunk from reloading forever.
function reloadOnFailure(factory) {
  return () =>
    factory().catch((error) => {
      const lastReload = getLastReload();
      if (
        lastReload !== null &&
        Date.now() - lastReload > RELOAD_COOLDOWN &&
        setLastReload(Date.now())
      ) {
        window.location.reload();
        // Keep suspense showing the loader until the reload happens
        return new Promise(() => {});
      }
      throw error;
    });
}

function lazyBase(factory) {
  const Component = lazy(reloadOnFailure(factory));
  return forwardRef((props, ref) => {
    return createElement(
      Suspense,
      {
        fallback: createElement(Loading)
      },
      createElement(
        Component,
        {
          ...props,
          ref
        }
      )
    )
  })
}

// Will return a function that will create our component
// once we are ready to use it
//
// Any call after will return the same initial component
export function lazyCreator(factory) {
  let initialised;
  return () => {
    return initialised = initialised || lazyBase(factory)
  }
}

export function superLazyRender(factory) {
  const Component = superLazy(factory);
  return (props) => createElement(Component, props);
}

export function superLazy(factory) {
  const creator = lazyCreator(factory);
  return forwardRef((props, ref) => {
    const Component = creator();
    return createElement(
      Component,
      {
        ...props,
        ref
      }
    );
  });
}

export default lazyBase;
